import {BuildGameConfigurations, ModeSettingEnum} from '@colonist/game-base-types'
import {UIAdController} from '../../../shared/scripts/ui_ad/UIAdController'
import {UIGameManager} from '../../../shared/scripts/ui_game/UIGameManager'
import {UIMGameToolTipController} from './ui_game_tooltip/UIMGameToolTipController'
import {UIMGameCanvasController} from './UIMGameCanvasController'
import {UIGameActionsController} from '../../../shared/scripts/ui_game/ui_game_actions/UIGameActionsController'
import {UIGameCardController} from '../../../shared/scripts/ui_game/ui_game_card/UIGameCardController'
import {UIMGameMapController} from './ui_game_map/UIMGameMapController'
import {UIMGamePlayersController} from './ui_game_players/UIMGamePlayersController'
import {UIGameTurnSoundController} from '../../../shared/scripts/ui_game/UIGameTurnSoundController'
import {UIMGameBaseDiceController} from './ui_game_dice/UIMGameBaseDiceController'
import {UIMGamePopupController} from './ui_game_popup/UIMGamePopupController'
import {UIMGameBarbarianInvasion} from './ui_game_cities_and_knights/UIMGameBarbarianInvasion'
import {UIMGameCityImprovement} from './ui_game_cities_and_knights/UIMGameCityImprovement'
import {UIMGameKnightController} from './ui_game_cities_and_knights/UIMGameKnightController'
import {UIMGameEventDiceController} from './ui_game_cities_and_knights/ui_game_cities_and_knights_dice/UIMGameEventDiceController'
import {UIGameFogTileController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameFogTileController'
import {UIGameGoldTileController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameGoldTileController'
import {UIGameMapChitController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameMapChitController'
import {UIGameMapCityWallController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameMapCityWallController'
import {UIGameMapPirateController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameMapPirateController'
import {UIGameMapShipController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameMapShipController'
import {UIMGamePlayerInformationLargestArmyController} from './ui_game_players/UIMGamePlayerInformationLargestArmyController'
import {UIMGamePlayerInformationLongestRoadController} from './ui_game_players/UIMGamePlayerInformationLongestRoadController'
import {UIMGamePlayerInformationProgressCardController} from './ui_game_players/UIMGamePlayerInformationProgressCardController'
import {UIMGameAnimationController} from './ui_game_animation/UIMGameAnimationController'
import {UIMGameLayout} from './UIMGameLayout'
import {UIGameController} from '../../../shared/scripts/ui_game/UIGameController'
import {GameState, NestedReadonly} from '@colonist/game-states'
import {UIGameMapRobberController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameMapRobberController'
import {UIGameMerchantController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameMapMerchantController'
import {UIReplayController} from '../../../shared/scripts/ui_replay/UIReplayController'
import {UIGamePauseController} from '../../../shared/scripts/ui_game/UIGamePauseController'
import {UIGameAchievementController} from '../../../shared/scripts/ui_game/ui_game_achievement/UIGameAchievementController'
import {UIGameInventorHighlightDiceController} from '../../../shared/scripts/ui_game/ui_game_map/UIGameInventorHighlightDiceController'
import {UIMDeviceHandler} from '../ui_html/UIMDeviceHandler'

export class UIMGameManager extends UIGameManager {
    animationController: UIMGameAnimationController

    constructor(adController: UIAdController) {
        super(adController)

        const body = document.getElementsByTagName('body')[0]
        body.classList.add('m-new-game')

        this.canvasController = new UIMGameCanvasController(this)
        this.toolTipController = new UIMGameToolTipController(this)
    }

    buildGame(configurations: BuildGameConfigurations) {
        this.canvasController.responsiveController.resizeScreen()
        this.gameController = new UIGameController(this, configurations)

        super.buildGame(configurations)

        const {
            mechanicDevelopmentCardsState,
            mechanicProgressCardsState,
            mechanicSettlementState,
            mechanicCityState,
            mechanicCityWallState,
            mechanicCityImprovementsState,
            mechanicRoadState,
            mechanicLongestRoadState,
            mechanicLargestArmyState,
            mechanicSpecialBuildPhaseState,
            mechanicRobberState,
            mechanicEventDiceState,
        } = configurations.gameState

        this.gameLayout = new UIMGameLayout(this)
        this.animationController = new UIMGameAnimationController(this)
        this.mapController = new UIMGameMapController(this) // Jan 28, 2021. Jeff: Map first so its below containers
        this.playersController = new UIMGamePlayersController(this,
            mechanicDevelopmentCardsState,
            mechanicProgressCardsState,
            mechanicSettlementState,
            mechanicCityState,
            mechanicCityWallState,
            mechanicCityImprovementsState,
            mechanicRoadState,
            mechanicLongestRoadState,
            mechanicLargestArmyState,
            mechanicSpecialBuildPhaseState)
        this.actionsController = new UIGameActionsController(this, mechanicSettlementState, mechanicCityState, mechanicRoadState)
        this.cardController = new UIGameCardController(this, mechanicRobberState)
        this.diceController = new UIMGameBaseDiceController(this, mechanicEventDiceState)
        new UIGameAchievementController(this)
        new UIGameTurnSoundController(this)
        new UIMGamePopupController(this)
        this.pauseController = new UIGamePauseController(this)
        new UIGameGoldTileController(this)

        this.buildOrderDependentControllers(configurations.gameState)

        const isColonistRush = this.gameController.gameSettings.modeSetting == ModeSettingEnum.ColonistRush4P
        if(isColonistRush) this.addColonistRushBranding()
    }

    initBeginnerMode() {
        super.initBeginnerMode()
        this.diceController?.initBeginnerModeHelper()
    }

    buildReplayView(replayController: UIReplayController): void {
        this.canvasController.removeResizeListener()
    }

    private buildOrderDependentControllers(gameState: NestedReadonly<GameState>) {
        this.mapController.createMap()
        this.buildOptionalMechanics(gameState)
        this.mapController.mapView.sortChildren()
        if(this.originalMapPosition != undefined) {
            this.mapController.mapView.scale.set(this.originalMapPosition.zoom.x, this.originalMapPosition.zoom.y)
            this.mapController.mapView.emit('zoomed', this.mapController.mapView.DEFAULT_ZOOM_EVENT)
            this.mapController.mapView.repositionMap(this.originalMapPosition.location)
        }
    }

    protected buildOptionalMechanics(gameState: NestedReadonly<GameState>) {
        if(gameState.mechanicCityWallState) {
            new UIGameMapCityWallController(this, gameState.mechanicCityWallState)
        }

        if(gameState.mechanicShipState) {
            new UIGameMapShipController(this, gameState.mechanicShipState, gameState.mechanicPirateState)
        }

        if(gameState.mechanicLongestRoadState) {
            new UIMGamePlayerInformationLongestRoadController(this, gameState.mechanicLongestRoadState)
        }

        if(gameState.mechanicLargestArmyState) {
            new UIMGamePlayerInformationLargestArmyController(this, gameState.mechanicLargestArmyState, gameState.mechanicDevelopmentCardsState)
        }

        if(gameState.mechanicRobberState) {
            new UIGameMapRobberController(this, gameState.mechanicRobberState)
        }

        if(gameState.mechanicPirateState) {
            new UIGameMapPirateController(this, gameState.mechanicPirateState)
        }

        if(gameState.mechanicEventDiceState) {
            new UIMGameEventDiceController(this, gameState.mechanicEventDiceState)
        }

        if(gameState.mechanicIslandExplorationState) {
            new UIGameMapChitController(this)
        }

        if(gameState.mapState.fogTileGroupStates) {
            new UIGameFogTileController(this, gameState.mapState.fogTileGroupStates)
        }

        if(gameState.mechanicKnightState) {
            new UIMGameKnightController(this, gameState.mechanicKnightState, gameState.mechanicRobberState, gameState.mechanicPirateState)
        }

        if(gameState.mechanicMerchantState) {
            new UIGameMerchantController(this, gameState.mechanicMerchantState)
        }

        if(gameState.mechanicBarbarianInvasionState && gameState.mechanicKnightState) {
            new UIMGameBarbarianInvasion(this, gameState.mechanicBarbarianInvasionState, gameState.mechanicKnightState)
        }

        if(gameState.mechanicProgressCardsState) {
            new UIMGamePlayerInformationProgressCardController(this)
            new UIGameInventorHighlightDiceController(this)
        }

        if(gameState.mechanicCityImprovementsState) {
            new UIMGameCityImprovement(this, gameState.mechanicCityImprovementsState, gameState.mechanicRobberState)
        }

        super.buildOptionalMechanics(gameState)
    }

    // eslint-disable-next-line class-methods-use-this
    openShareLinkMobileModal(link: string) {
        const url = new URL(link)
        UIMDeviceHandler.sendShareLink(url.href)
    }
}
